import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

interface ElaraPanelState {
    isPanelOpen: boolean
    sessionId: string | null
    togglePanel: () => void
    openPanel: () => void
    closePanel: () => void
    setSessionId: (id: string | null) => void
    newChat: () => void
    /** Prompt to auto-send in a fresh conversation, set by `askElara`; cleared once sent. */
    pendingPrompt: string | null
    /** Files sent with {@link pendingPrompt} (attached in the search). */
    pendingFiles: File[]
    /** Increments on every `askElara` so the chat remounts on a fresh conversation. */
    pendingPromptNonce: number
    /** Opens the panel on a new conversation and sends `prompt` (with optional files) as its first message. */
    askElara: (prompt: string, files?: File[]) => void
    clearPendingPrompt: () => void
}

const ElaraPanelContext = createContext<ElaraPanelState | null>(null)

/** localStorage key persisting the panel's current conversation across reloads/panel toggles. */
const PANEL_SESSION_STORAGE_KEY = 'elara-panel-session'

function readStoredSessionId(): string | null {
    try {
        return localStorage.getItem(PANEL_SESSION_STORAGE_KEY)
    } catch {
        return null
    }
}

function writeStoredSessionId(sessionId: string | null) {
    try {
        if (sessionId) localStorage.setItem(PANEL_SESSION_STORAGE_KEY, sessionId)
        else localStorage.removeItem(PANEL_SESSION_STORAGE_KEY)
    } catch {
        // Storage unavailable (private mode) — session just won't survive a reload.
    }
}

export function ElaraPanelProvider({ children }: { children: React.ReactNode }) {
    const [isPanelOpen, setIsPanelOpen] = useState(false)
    // Restore the last panel conversation so reopening the FAB resumes it (same store as /medconnect-agent).
    const [sessionId, setSessionIdState] = useState<string | null>(readStoredSessionId)

    const setSessionId = useCallback((id: string | null) => {
        setSessionIdState(id)
        writeStoredSessionId(id)
    }, [])

    const togglePanel = useCallback(() => setIsPanelOpen((p) => !p), [])
    const openPanel = useCallback(() => setIsPanelOpen(true), [])
    const closePanel = useCallback(() => setIsPanelOpen(false), [])

    const newChat = useCallback(() => setSessionId(null), [setSessionId])

    const [pendingPrompt, setPendingPrompt] = useState<string | null>(null)
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    const [pendingPromptNonce, setPendingPromptNonce] = useState(0)

    const askElara = useCallback(
        (prompt: string, files: File[] = []) => {
            setSessionId(null)
            setPendingPrompt(prompt)
            setPendingFiles(files)
            setPendingPromptNonce((n) => n + 1)
            setIsPanelOpen(true)
        },
        [setSessionId],
    )

    const clearPendingPrompt = useCallback(() => {
        setPendingPrompt(null)
        setPendingFiles([])
    }, [])

    // Ctrl+E to toggle panel
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'e' && e.ctrlKey && !e.altKey && !e.shiftKey) {
                e.preventDefault()
                setIsPanelOpen((p) => !p)
            }
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [])

    return (
        <ElaraPanelContext.Provider
            value={{
                isPanelOpen,
                sessionId,
                togglePanel,
                openPanel,
                closePanel,
                setSessionId,
                newChat,
                pendingPrompt,
                pendingFiles,
                pendingPromptNonce,
                askElara,
                clearPendingPrompt,
            }}
        >
            {children}
        </ElaraPanelContext.Provider>
    )
}

export function useElaraPanel(): ElaraPanelState | null {
    return useContext(ElaraPanelContext)
}
